import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Zap, CheckCircle, XCircle, ExternalLink, AlertCircle } from 'lucide-react';
import api from '../../services/api';

const DEFAULT_FEATURES = [
  { key: 'sales_enabled', label: 'Sales' },
  { key: 'purchases_enabled', label: 'Purchases' },
  { key: 'catalogue_enabled', label: 'Catalogue' },
  { key: 'cash_bank_enabled', label: 'Cash & Bank' },
  { key: 'gst_enabled', label: 'GST' },
  { key: 'reports_enabled', label: 'Reports' },
  { key: 'pos_enabled', label: 'POS' },
  { key: 'field_tracking_enabled', label: 'Field Tracking' },
  { key: 'attendance_enabled', label: 'Attendance' },
  { key: 'visit_tasks_enabled', label: 'Visit Tasks' },
  { key: 'collections_enabled', label: 'Collections' },
  { key: 'routes_enabled', label: 'Routes' },
];

/** Convert a key like 'field_tracking_enabled' to 'Field Tracking' */
const keyToLabel = (key) =>
  key.replace(/_enabled$/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** Extract unique _enabled keys from plan objects, preserving DEFAULT_FEATURES order */
const deriveFeaturesFromPlans = (plans) => {
  if (!plans || plans.length === 0) return DEFAULT_FEATURES;

  const enabledKeys = new Set();
  plans.forEach(plan => {
    Object.keys(plan).forEach(key => {
      if (key.endsWith('_enabled') && typeof plan[key] === 'boolean') {
        enabledKeys.add(key);
      }
    });
  });

  if (enabledKeys.size === 0) return DEFAULT_FEATURES;

  // Start with default features that exist in the data, then append any new ones
  const defaultKeys = new Set(DEFAULT_FEATURES.map(f => f.key));
  const ordered = DEFAULT_FEATURES.filter(f => enabledKeys.has(f.key));
  enabledKeys.forEach(key => {
    if (!defaultKeys.has(key)) {
      ordered.push({ key, label: keyToLabel(key) });
    }
  });

  return ordered.length > 0 ? ordered : DEFAULT_FEATURES;
};

const FeatureFlags = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/api/admin/plans')
      .then(res => {
        const data = res.data;
        const planList = Array.isArray(data) ? data : (data.plans || []);
        setPlans(planList);
        setFeatures(deriveFeaturesFromPlans(planList));
      })
      .catch(() => setError('Failed to load plans'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-yellow-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <AlertCircle className="w-10 h-10 text-red-400" />
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feature Flags</h1>
          <p className="text-gray-600 mt-1">Feature availability per plan (read-only)</p>
        </div>
        <button
          onClick={() => navigate('/admin/plans-v2')}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-medium"
        >
          <ExternalLink className="w-4 h-4" />
          Edit Plans
        </button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-600" />
            <CardTitle>Feature Matrix by Plan</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {plans.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No plans found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium text-gray-600 min-w-[140px]">Feature</th>
                    {plans.map(plan => (
                      <th key={plan.id} className="text-center p-3 font-medium text-gray-800 min-w-[100px]">
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {features.map(feature => (
                    <tr key={feature.key} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-700 font-medium">{feature.label}</td>
                      {plans.map(plan => (
                        <td key={plan.id} className="p-3 text-center">
                          {plan[feature.key] ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-300 mx-auto" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FeatureFlags;
